import styled from '@emotion/styled';
import { ObjectId } from '@racemap/sdk/schema/base';
import { UserOverviewSchema } from '@racemap/sdk/schema/user';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { isEmptyString } from '@racemap/utilities/functions/utils';
import { isValidEmail } from '@racemap/utilities/functions/validation';
import { Select as ASelect, Flex, type RefSelectProps, Space, Typography } from 'antd';
import type { BaseOptionType } from 'antd/es/select';
import { forwardRef, type ReactNode, useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { useControlled, useUserInfo } from '../../../lib/customHooks';
import { IconPlus, IconRemove } from '../Icons';
import { Plg } from '../Placeholder';
import { UserAvatar } from './UserAvatar';
import { UserInfos } from './UserInfos';

const { Text } = Typography;

export interface UserInfo extends BaseOptionType {
  email: string;
  name?: string;
  id: ObjectId;
  parentId?: ObjectId | null;
}

type Props = {
  value?: ObjectId | null;
  onChange?: (user: ObjectId | null, userInfo: UserInfo | null) => void;
  placeholder?: string;
  disabled?: boolean;
  extraOptions?: Array<UserInfo>;
  allowClear?: boolean;
  clearAfterChoosing?: boolean;
  filterUserIds?: Array<ObjectId>;
  action?: 'add' | 'remove' | 'search';
  maxWidth?: string;
  autoFocus?: boolean;
  /** Search text. Pass together with onSearchChange to control it from outside. */
  searchValue?: string;
  onSearchChange?: (search: string) => void;
};

export const UserChooser = forwardRef<RefSelectProps, Props>(
  (
    {
      value,
      onChange,
      disabled = false,
      allowClear = true,
      clearAfterChoosing = false,
      extraOptions = [],
      action = 'search',
      filterUserIds = [],
      placeholder = 'Search for email or name...',
      maxWidth,
      autoFocus = false,
      searchValue: searchValueProp,
      onSearchChange,
    },
    ref,
  ) => {
    const [searchValue, setSearchValue] = useControlled<string>({
      controlledValue: searchValueProp,
      initialValue: '',
      name: 'UserChooser.search',
      onChange: onSearchChange,
    });
    const [valueControlled, setValue] = useControlled<ObjectId | null>({
      controlledValue: value,
      initialValue: null,
      onChange,
      name: 'UserChooser',
    });
    const [selectedUserId, setSelectedUserId] = useState<ObjectId | null>(null);
    const { data: users, isLoading } = useSWR(['searchUsers', searchValue], loadUsers, {
      keepPreviousData: true,
      fallbackData: [],
    });

    useEffect(() => {
      if (valueControlled == null) return;
      setSelectedUserId(valueControlled);
    }, [valueControlled]);

    const handleOnChange = async (value: ObjectId | string | null) => {
      if (value == null) {
        setValue(null, null);
        return;
      }

      const extraValue = extraOptions.find((opt) => opt.value === value);
      const userInfo = users.find((opt) => opt.id.toHexString() === value) || null;
      if (extraValue != null) {
        // if the value is one of the extra options, we set it directly
        setValue(extraValue.id, userInfo);
        setSelectedUserId(extraValue.id);
        if (onChange) onChange(extraValue.id, userInfo);
        return;
      }

      // handle that choosed option holds only a valid email
      if (isValidEmail(value)) {
        const user = await apiClient.userLookup({ email: value });
        if (user) {
          setValue(user.id, userInfo);
          setSelectedUserId(user.id);
        }
        return;
      }
      setValue(new ObjectId(value), userInfo);

      if (clearAfterChoosing) {
        setValue(null);
      } else {
        setSelectedUserId(new ObjectId(value));
      }
    };

    // if showSelf false, we filter out the current user
    const usersFiltered = [...extraOptions, ...users].filter(
      (u) => !filterUserIds.some((fU) => fU.equals(u.id)),
    );

    // if we found no user but the search value is a valid email, we add it to the options
    const options =
      usersFiltered.length === 0 && isValidEmail(searchValue)
        ? [{ value: searchValue, label: '', email: searchValue, name: '', id: null }]
        : usersFiltered;

    return (
      <Select
        ref={ref}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={allowClear}
        showSearch
        searchValue={searchValue}
        onSearch={setSearchValue}
        value={valueControlled?.toHexString() || null}
        // @ts-expect-error wrong lib types
        onChange={handleOnChange}
        loading={isLoading}
        filterOption={false}
        options={options}
        // @ts-expect-error wrong lib types
        optionRender={(props) => <OptionComponent extraOptions={extraOptions} {...props} />}
        labelRender={() => <ValueComponent userId={selectedUserId} extraOptions={extraOptions} />}
        suffixIcon={getSuffixIcon(action)}
        maxWidth={maxWidth}
        autoFocus={autoFocus}
      />
    );
  },
);

UserChooser.displayName = 'UserChooser';

const getSuffixIcon = (action: 'add' | 'remove' | 'search') => {
  switch (action) {
    case 'add':
      return <IconPlus />;
    case 'remove':
      return <IconRemove />;
    case 'search':
      return false;
  }
};

const apiClient = RacemapAPIClient.fromWindowLocation();
const loadUsers = async ([, query]: [string, string]): Promise<Array<UserInfo>> => {
  const queryTrimmed = query.trim().toLowerCase();
  const users = (
    await apiClient.getUsers({
      search: queryTrimmed,
      limit: 4,
    })
  ).data.map((u) => UserOverviewSchema.parse(u));

  // update cache user to prevent user info component from fetching the same user
  await Promise.all(
    users.map((u) =>
      mutate(['GET', 'USER_INFO', u.id.toHexString()], u, {
        populateCache: true,
        revalidate: false,
      }),
    ),
  );

  return users.map((u) => ({
    value: u.id.toHexString(),
    label: u.name || u.email || u.id.toHexString(),
    ...u,
  }));
};

const OptionComponent = ({
  data,
  extraOptions,
}: {
  data: UserInfo;
  extraOptions: Array<UserInfo>;
}): ReactNode => {
  if (!('email' in data) || isEmptyString(data.email)) return null;
  const extraOption = extraOptions.find((opt) => opt.id.equals(data.id));
  if (extraOption != null) {
    return (
      <Flex gap={8}>
        <UserAvatar />
        <Flex vertical>
          <Text strong>{extraOption.label}</Text>
          <Text type="secondary">{extraOption.email}</Text>
        </Flex>
      </Flex>
    );
  }

  return <UserInfos userId={data.id} email={data.email} />;
};

const ValueComponent = ({
  userId,
  extraOptions,
}: {
  userId: ObjectId | null;
  extraOptions: Array<UserInfo>;
}) => {
  const { error, user, isLoading } = useUserInfo(userId);

  // if is extra option
  const extraOption = extraOptions.find((opt) => opt.id.equals(userId));
  if (extraOption != null) {
    return <UserValueField name={extraOption.label} email={extraOption.email} />;
  }

  // if user is not loaded yet, show placeholder
  if (isLoading && user === undefined)
    return (
      <Space>
        <Plg w="150px" /> <Plg w="200px" />{' '}
      </Space>
    );
  if (user == null) return null;
  if (error) return <Text type="danger">Error</Text>;

  return <UserValueField name={user.name} email={user.email} />;
};

const UserValueField = ({ name, email }: { name?: string; email: string }) => (
  <Space title={`${name}\n${email}`}>
    <strong>{name}</strong>
    <Text>{email}</Text>
  </Space>
);

interface StyledSelectProps {
  maxWidth?: string;
}

const Select = styled(ASelect)<StyledSelectProps>`
  width: 100%;
 ${({ maxWidth }) => (maxWidth ? `max-width: ${maxWidth};` : '')}
`;
